import { useQuery } from '@tanstack/react-query'
import { listAccounts } from '@/api/accounts'
import { listCategories } from '@/api/categories'
import { listGoals } from '@/api/goals'
import { listMembers } from '@/api/members'
import { listContacts } from '@/api/people'
import { currentMonth, toMemberOptions } from '@/lib/transactionDateRangeFilter'
import type { TransactionLinkFilters, TransactionType } from '@/lib/transactionsLink'
import { periodLabel } from './transactionDisplay'

type Option = { id: string; label: string }

/** Names for every id a filter can hold, from the lists other screens already cache. */
export function useFilterOptions() {
  const { data: accounts } = useQuery({ queryKey: ['accounts'], queryFn: () => listAccounts() })
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => listCategories(),
  })
  const { data: members } = useQuery({ queryKey: ['members'], queryFn: () => listMembers() })
  const { data: contacts } = useQuery({
    queryKey: ['contacts', { includeArchived: true }],
    queryFn: () => listContacts(true),
  })
  const { data: goals } = useQuery({ queryKey: ['goals'], queryFn: () => listGoals() })
  return {
    accounts: (accounts?.accounts ?? []).map((a) => ({ id: a.id, label: a.name })),
    categories: (categories?.categories ?? []).map((c) => ({ id: c.id, label: c.name })),
    members: toMemberOptions(members),
    contacts: (contacts ?? []).map((c) => ({ id: c.id, label: c.name, archived: c.archived })),
    // Only earmark goals have contributions to list; a dedicated goal links its account instead.
    goals: [...(goals?.active ?? []), ...(goals?.paused ?? []), ...(goals?.completed ?? [])]
      .filter((g) => g.trackingMode === 'EARMARK')
      .map((g) => ({ id: g.id, label: g.name })),
    loaded: {
      accounts: !!accounts,
      categories: !!categories,
      members: !!members,
      contacts: !!contacts,
      goals: !!goals,
    },
  }
}

export type FilterOptions = ReturnType<typeof useFilterOptions>

const TYPE_LABELS: Record<TransactionType, string> = {
  EXPENSE: 'Spent',
  INCOME: 'Income',
  TRANSFER: 'Transfers',
  REFUND: 'Refunds',
  PERSON_PAYMENT: 'People payments',
}
export const PILLS: [string, TransactionType | undefined][] = [
  ['All', undefined],
  ['Spent', 'EXPENSE'],
  ['Income', 'INCOME'],
  ['Transfers', 'TRANSFER'],
]

export interface FilterChip {
  key: string
  label: string
  remove: Partial<TransactionLinkFilters>
}

function nameOf(list: Option[], id: string, loaded: boolean, missing: string): string {
  const found = list.find((o) => o.id === id)
  if (found) return found.label
  // Until the list arrives the id is simply not named yet; once it has, a miss means it's gone.
  return loaded ? missing : '…'
}

/** One removable chip per active filter beyond the month and the type pills. */
export function filterChips(f: TransactionLinkFilters, o: FilterOptions): FilterChip[] {
  const chips: FilterChip[] = []
  if (!f.month) {
    chips.push({
      key: 'period',
      label: periodLabel(f),
      remove: {
        date: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        spendMonthFrom: undefined,
        spendMonthTo: undefined,
        month: currentMonth(),
      },
    })
  }
  if (f.type && !PILLS.some(([, t]) => t === f.type)) {
    chips.push({ key: 'type', label: `Type: ${TYPE_LABELS[f.type]}`, remove: { type: undefined } })
  }
  if (f.financialAccountId) {
    chips.push({
      key: 'account',
      label: `Account: ${nameOf(o.accounts, f.financialAccountId, o.loaded.accounts, 'Removed account')}`,
      remove: { financialAccountId: undefined },
    })
  }
  if (f.categoryId) {
    chips.push({
      key: 'category',
      label: `Category: ${nameOf(o.categories, f.categoryId, o.loaded.categories, 'Removed category')}`,
      remove: { categoryId: undefined },
    })
  }
  if (f.attributedToHouseholdMemberId) {
    chips.push({
      key: 'member',
      label: `Member: ${nameOf(o.members, f.attributedToHouseholdMemberId, o.loaded.members, 'Removed member')}`,
      remove: { attributedToHouseholdMemberId: undefined },
    })
  }
  if (f.contactId) {
    chips.push({
      key: 'person',
      label: `Person: ${nameOf(o.contacts, f.contactId, o.loaded.contacts, 'Removed person')}`,
      remove: { contactId: undefined },
    })
  }
  if (f.goalId) {
    chips.push({
      key: 'goal',
      label: `Goal: ${nameOf(o.goals, f.goalId, o.loaded.goals, 'Removed goal')}`,
      remove: { goalId: undefined },
    })
  }
  if (f.isCapitalEvent) {
    chips.push({
      key: 'capital',
      label: 'One-off events only',
      remove: { isCapitalEvent: undefined },
    })
  }
  if (f.reconciled === false) {
    chips.push({
      key: 'unreconciled',
      label: 'Not reconciled',
      remove: { reconciled: undefined },
    })
  }
  return chips
}

/** The hero's scope line: the chip names without their "Category:" style prefixes. */
export function scopeLabels(chips: FilterChip[]): string[] {
  return chips.filter((c) => c.key !== 'period').map((c) => c.label.replace(/^[^:]+: /, ''))
}
